// src/pages/PartnerRegisterPage.tsx
import React, { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  TextField,
  Button,
  Typography,
  Paper,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { registerPartnerApi } from "src/api/delegate/delegateApi";
import { snackbarStore } from "src/store/snackbarStore";

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  padding: 20px;
`;

const FormPaper = styled(Paper)`
  padding: 40px;
  max-width: 450px;
  width: 100%;
`;

const Title = styled(Typography)`
  text-align: center;
  margin-bottom: 32px !important;
  font-weight: 600 !important;
`;

const FormField = styled(TextField)`
  margin-bottom: 20px !important;
`;

const SubmitButton = styled(Button)`
  margin-top: 16px !important;
  height: 48px;
`;

function decodeEmail(encryptedEmail: string): string | null {
  try {
    // URL-safe Base64를 일반 Base64로 변환
    const base64 = encryptedEmail.replace(/-/g, "+").replace(/_/g, "/");
    // Base64 디코딩
    const decoded = atob(base64);
    // 이메일 형식인지 확인
    if (decoded.includes("@")) {
      return decoded;
    }
    return null;
  } catch {
    return null;
  }
}

const PartnerRegisterPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const addSnackbar = snackbarStore.getState().addSnackbar;

  const token = searchParams.get("token") || "";
  const encryptedEmail = searchParams.get("email") || "";
  const decodedEmail = useMemo(() => decodeEmail(encryptedEmail), [encryptedEmail]);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    name?: string;
    password?: string;
    passwordConfirm?: string;
  }>({});

  const validateForm = () => {
    const newErrors: typeof errors = {};

    if (!name.trim()) {
      newErrors.name = "이름을 입력해주세요.";
    }

    if (!password) {
      newErrors.password = "비밀번호를 입력해주세요.";
    } else if (password.length < 8) {
      newErrors.password = "비밀번호는 8자 이상이어야 합니다.";
    }

    if (!passwordConfirm) {
      newErrors.passwordConfirm = "비밀번호 확인을 입력해주세요.";
    } else if (password !== passwordConfirm) {
      newErrors.passwordConfirm = "비밀번호가 일치하지 않습니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (!token || !encryptedEmail) {
      addSnackbar({ message: "유효하지 않은 초대 링크입니다.", severity: "error" });
      return;
    }

    try {
      setLoading(true);
      const res = await registerPartnerApi({
        token,
        encryptedEmail,
        name: name.trim(),
        password,
      });

      if (res.statusCode === 1) {
        addSnackbar({ message: "회원가입이 완료되었습니다.", severity: "success" });
        navigate("/login", { replace: true });
      }
    } catch (err: any) {
      addSnackbar({
        message: err?.message || "회원가입에 실패했습니다.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // 토큰이나 이메일이 없으면 에러 표시
  if (!token || !encryptedEmail) {
    return (
      <Container>
        <FormPaper elevation={3}>
          <Title variant="h5">잘못된 접근</Title>
          <Typography color="error" textAlign="center">
            유효하지 않은 초대 링크입니다.
            <br />
            이메일로 받은 초대 링크를 다시 확인해주세요.
          </Typography>
          <SubmitButton
            fullWidth
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{ mt: 3 }}
          >
            로그인 페이지로 이동
          </SubmitButton>
        </FormPaper>
      </Container>
    );
  }

  return (
    <Container>
      <FormPaper elevation={3}>
        <Title variant="h5">입점사 회원가입</Title>

        <Box component="form" onSubmit={handleSubmit}>
          {decodedEmail && (
            <FormField fullWidth label="이메일" value={decodedEmail} disabled variant="outlined" />
          )}
          <FormField
            fullWidth
            label="이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            variant="outlined"
            placeholder="이름을 입력해주세요"
          />

          <FormField
            fullWidth
            label="비밀번호"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!errors.password}
            helperText={errors.password || "8자 이상 입력해주세요"}
            variant="outlined"
            placeholder="비밀번호를 입력해주세요"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormField
            fullWidth
            label="비밀번호 확인"
            type={showPasswordConfirm ? "text" : "password"}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            error={!!errors.passwordConfirm}
            helperText={errors.passwordConfirm}
            variant="outlined"
            placeholder="비밀번호를 다시 입력해주세요"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    edge="end"
                  >
                    {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <SubmitButton type="submit" fullWidth variant="contained" disabled={loading} size="large">
            {loading ? <CircularProgress size={24} color="inherit" /> : "회원가입"}
          </SubmitButton>
        </Box>
      </FormPaper>
    </Container>
  );
};

export default PartnerRegisterPage;
